import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { ReportTemplate, useDisableReportSchedule } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';

interface ReportScheduleDeleteProps {
  reportTemplate: ReportTemplate;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReportScheduleDelete = ({
  reportTemplate,
  showDialog,
  setShowDialog,
}: ReportScheduleDeleteProps) => {
  const { messages } = useLocalization();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate } = useDisableReportSchedule();
  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const templateName = reportTemplate.tplName;

    if (!reportTemplate.tplUuid) {
      toast({
        className: 'delete-report-template-error-toast',
        title: messages.genericToastTitleError,
        description: messages.reportsDeleteScheduleFailedNoIdMessage.replace(
          '{label}',
          templateName
        ),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    const params = {
      serverUid: '1b1b25bc-34f5-4917-9e1a-d6af13163b09',
      name: reportTemplate.tplName,
      templateUid: reportTemplate.tplUuid,
      interval: 'disabled',
      intervalStart: reportTemplate.schedule?.intervalStart,
      endTime: '12 Mar 2024',
    };

    mutate(params, {
      onSuccess: () => {
        toast({
          className: 'delete-report-template-schedule-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDeleteScheduleSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: (error: any) => {
        if (error.response?.status === 403) {
          toast({
            className: 'delete-report-template-schedule-error-toast',
            title: messages.genericToastTitleForbidden,
            description:
              messages.reportsDeleteScheduleFailedMissingPermissionsMessage.replace(
                '{label}',
                templateName
              ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        } else {
          toast({
            className: 'delete-report-template-error-toast',
            title: messages.genericToastTitleError,
            description:
              messages.reportsDeleteScheduleFailedGenericMessage.replace(
                '{label}',
                templateName
              ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        }
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="delete-report-schedule-template-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.reportsDeleteScheduleTitle}</DialogTitle>
          <DialogDescription>
            {messages.reportsDeleteScheduleWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.reportsDeleteScheduleNameLabel}
            </Label>
            <Label htmlFor="report-template-name">
              {reportTemplate.tplName}
            </Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              onClick={(event) => onDelete(event)}
              variant="destructive"
              id="report-schedule-confirm-delete-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
